import { doc, getDoc, Timestamp, updateDoc, increment, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import React, { useState, useRef } from 'react';
import { auth } from '../firebase';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Divider, Button, CircularProgress, TextField, Grid, Stack, MenuItem } from '@mui/material';
import { Elements, useStripe, useElements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { stripePromise } from '../stripe';
// import { MaxRectsPacker } from 'maxrects-packer';
// import { DPI, SHEET_WIDTH_IN, SHEET_HEIGHT_IN } from '../config/dtfConfig';
import { changeDpiDataUrl } from 'changedpi';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';
import { roundMoney, roundSize } from '../utils/money';

const envFlag = (value) => String(value || '').toLowerCase() === 'true';

const STRIPE_TEST_MODE = envFlag(process.env.REACT_APP_STRIPE_TEST_MODE);
const USE_EMULATORS = envFlag(process.env.REACT_APP_USE_EMULATORS);
const FUNCTIONS_BASE_URL = (process.env.REACT_APP_FUNCTIONS_BASE_URL || '').replace(/\/$/, '');

const REMOTE_PAYMENT_INTENT_URL = 'https://createpaymentintent-7tdimhpb4a-uc.a.run.app';
const REMOTE_SUBMIT_ORDER_URL = 'https://submitorder-7tdimhpb4a-uc.a.run.app';
const ENV_PAYMENT_INTENT_URL = process.env.REACT_APP_CREATE_PAYMENT_INTENT_URL;
const ENV_SUBMIT_ORDER_URL = process.env.REACT_APP_SUBMIT_ORDER_URL;

const PAYMENT_INTENT_BASE_URL = ENV_PAYMENT_INTENT_URL || REMOTE_PAYMENT_INTENT_URL;
const SUBMIT_ORDER_BASE_URL = ENV_SUBMIT_ORDER_URL || REMOTE_SUBMIT_ORDER_URL;

const PAYMENT_INTENT_ENDPOINT = USE_EMULATORS && FUNCTIONS_BASE_URL
  ? `${FUNCTIONS_BASE_URL}/createPaymentIntent`
  : PAYMENT_INTENT_BASE_URL;
const SUBMIT_ORDER_ENDPOINT = USE_EMULATORS && FUNCTIONS_BASE_URL
  ? `${FUNCTIONS_BASE_URL}/submitOrder`
  : SUBMIT_ORDER_BASE_URL;

const PAYMENT_INTENT_ENDPOINTS = USE_EMULATORS
  ? [PAYMENT_INTENT_ENDPOINT]
  : Array.from(new Set([
    PAYMENT_INTENT_ENDPOINT,
    REMOTE_PAYMENT_INTENT_URL,
  ]));
const SUBMIT_ORDER_ENDPOINTS = USE_EMULATORS
  ? [SUBMIT_ORDER_ENDPOINT]
  : Array.from(new Set([
    SUBMIT_ORDER_ENDPOINT,
    REMOTE_SUBMIT_ORDER_URL,
  ]));

// ---- IndexedDB helpers for large checkout payloads ----
function openCheckoutDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('dtfSessionDb', 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('sessions')) db.createObjectStore('sessions');
      if (!db.objectStoreNames.contains('checkout')) db.createObjectStore('checkout');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
function loadCheckoutPayload(sessionId) {
  return openCheckoutDb().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction('checkout', 'readonly');
    const getReq = tx.objectStore('checkout').get(sessionId || 'default');
    getReq.onsuccess = () => resolve(getReq.result || null);
    getReq.onerror = () => reject(getReq.error);
  }));
}
async function removeCheckoutPayload(sessionId) {
  const db = await openCheckoutDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('checkout', 'readwrite');
    tx.objectStore('checkout').delete(sessionId || 'default');
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * @typedef {Object} Window
 * @property {function(...any): void} gtag
 */

/**
 * @type {function(...any): void}
 */
const gtag = (typeof window !== 'undefined' && window.gtag) ? window.gtag : () => { };


// Error code to user-visible message mapping
const ERROR_MESSAGES = {
  PAYMENT_INTENT_FAILED: 'Payment could not be processed. Please try again or use a different card.',
  NETWORK_ERROR: 'Network error occurred. Check your connection and try again.',
  SESSION_UNAVAILABLE: 'Payment session unavailable. Please wait a moment and retry.',
  ORDER_SUBMIT_FAILED: 'Failed to submit order. Please contact support.',
  // add more codes as needed
};
// Limit concurrent async uploads
async function runWithLimit(limit, tasks) {
  const results = new Array(tasks.length);
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (true) {
      const idx = i++;
      if (idx >= tasks.length) break;
      results[idx] = await tasks[idx]();
    }
  });
  await Promise.all(workers);
  return results;
}

function computeRoundedItemPricing(widthIn, heightIn, rate, quantity) {
  const safeWidth = Number(widthIn) || 0;
  const safeHeight = Number(heightIn) || 0;
  const safeRate = Number(rate) || 0;
  const safeQuantity = Math.max(0, Number(quantity) || 0);
  const perPiece = roundMoney(safeWidth * safeHeight * safeRate);
  const total = roundMoney(perPiece * safeQuantity);
  return { perPiece, total };
}

const EMAIL_PREVIEW_TYPES = [
  { key: 'order-confirmation', label: 'Order confirmation' },
  { key: 'order-completion', label: 'Order completion' },
  { key: 'verification', label: 'Email verification' },
];

function getEmailPreview(type, { displayName, verificationLink, orderPreview } = {}) {
  if (type === 'order-completion') {
    const preview = orderPreview || {};
    const customerName = preview.customerName || 'Customer';
    const fallbackOrderNumber = 'SOD-' + String(Date.now()).slice(-5);
    const fallbackOrderDate = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const fullOrderId = preview.orderId || '';
    const orderNumber = preview.orderNumber || (fullOrderId ? String(fullOrderId).slice(0, 8) : fallbackOrderNumber);
    const orderDate = preview.orderDate || fallbackOrderDate;
    const orderUrl = fullOrderId
      ? `https://www.sodtf.com/profile/orders/${encodeURIComponent(fullOrderId)}`
      : 'https://sodtf.com/profile/orders/demo';

    const items = Array.isArray(preview.items) && preview.items.length
      ? preview.items
      : [
        {
          name: 'Custom DTF Gang Sheet',
          variant: '22" × 60" · Full Color',
          quantity: 1,
          lineTotal: 48,
        },
        {
          name: 'Logo Transfers Pack',
          variant: '4" × 4" · 12 pieces',
          quantity: 1,
          lineTotal: 24,
        },
      ];

    const itemsRowsHtml = items
      .map((item) => {
        const name = item.name || 'Custom DTF Item';
        const variant = item.variant || '';
        const quantity = typeof item.quantity === 'number' ? item.quantity : 1;
        const totalValue =
          typeof item.lineTotal === 'number'
            ? item.lineTotal.toFixed(2)
            : '0.00';
        return `
                        <tr>
                          <td style="padding:10px 12px; font-size:13px; color:#111827; border-bottom:1px solid #f3f4f6;">
                            <div style="font-weight:500;">${name}</div>
                            ${variant
            ? `<div style="font-size:12px; color:#6b7280; margin-top:2px;">${variant}</div>`
            : ''
          }
                          </td>
                          <td align="center" style="padding:10px 8px; font-size:13px; color:#374151; border-bottom:1px solid #f3f4f6;">
                            ${quantity}
                          </td>
                          <td align="right" style="padding:10px 12px; font-size:13px; color:#111827; font-weight:500; border-bottom:1px solid #f3f4f6;">
                            $${totalValue}
                          </td>
                        </tr>`;
      })
      .join('');

    const subtotalValue =
      typeof preview.subtotal === 'number'
        ? preview.subtotal.toFixed(2)
        : '0.00';
    const processingValue =
      typeof preview.processing === 'number'
        ? preview.processing.toFixed(2)
        : '0.00';
    const totalValue =
      typeof preview.total === 'number'
        ? preview.total.toFixed(2)
        : '0.00';

    return {
      subject: 'Your See Our Designs order is complete!',
      html: `
      <!DOCTYPE html>
<html lang="en" style="margin:0; padding:0;">
  <head>
    <meta charset="UTF-8" />
    <title>Order Completed – See Our Designs</title>
  </head>
  <body style="margin:0; padding:0; background:#f1f2f6;">
    <!-- Full-width background wrapper -->
    <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background:#f1f2f6; padding:32px 12px;">
      <tr>
        <td align="center">
          <!-- Main container -->
          <table width="640" border="0" cellspacing="0" cellpadding="0" style="max-width:640px; width:100%; font-family:-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">

            <!-- Top logo / header -->
            <tr>
              <td style="padding:0 0 20px 0;" align="center">
                <img src="https://www.sodtf.com/static/media/sodtf%20logo%20dark%20stacked.31c245b9a577db207ca1.png" alt="See Our Designs" style="max-width:150px; height:auto; display:block;" />
              </td>
            </tr>

            <!-- Card -->
            <tr>
              <td>
                <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background:#ffffff; border-radius:14px; border:1px solid #d8dbe2; box-shadow:0 8px 20px rgba(15,23,42,0.08); overflow:hidden;">

                  <!-- Status strip -->
                  <tr>
                    <td style="background: linear-gradient(90deg, rgb(148,201,64), rgb(121,165,52)); padding:10px 20px; color:#e5e7eb; font-size:11px; letter-spacing:0.12em; text-transform:uppercase;">
                      <span style="opacity:0.8;">Order Status</span>
                      <span style="float:right; background:rgb(90, 104, 114); padding:3px 10px; border-radius:999px; font-weight:500; font-size:11px;">
                        Completed
                      </span>
                    </td>
                  </tr>

                  <!-- Main content -->
                  <tr>
                    <td style="padding:28px 28px 24px 28px;">

                      <!-- Greeting + info -->
                      <h1 style="margin:0 0 8px 0; font-size:22px; color:#111827; font-weight:650;">
                        Thank you, ${customerName}!
                      </h1>
                      <p style="margin:0 0 6px 0; font-size:13px; color:#6b7280; text-transform:uppercase; letter-spacing:0.11em;">
                        Order <span style="font-weight:600; color:#111827;">#${orderNumber}</span> · Completed on ${orderDate}
                      </p>

                      <p style="margin:16px 0 20px 0; font-size:14px; line-height:1.7; color:#4b5563;">
                        Your DTF order has been printed, packed, and is ready for in-person pickup at our store.
                        Below you’ll find a summary of your order and the pickup details. If anything doesn’t look right, just reply to this email and we’ll help you out.
                      </p>

                      <!-- Order timeline -->
                      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin:20px 0 24px 0;">
                        <tr>
                          <td style="font-size:11px; color:#6b7280; text-transform:uppercase; letter-spacing:0.14em; padding-bottom:6px;">
                            Order Timeline
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <!-- Progress bar -->
                            <div style="width:100%; background:#e5e7eb; border-radius:999px; height:6px; overflow:hidden; margin-bottom:6px;">
                              <!-- 100% filled because this is a completion email -->
                              <div style="width:100%; height:6px; border-radius:999px; background:linear-gradient(90deg,rgb(90, 104, 114),rgb(148, 201, 64));"></div>
                            </div>

                            <!-- Step labels -->
                            <table width="100%" border="0" cellspacing="0" cellpadding="0">
                              <tr>
                                <td width="33.33%" align="left" style="font-size:11px; color:rgb(90, 104, 114); font-weight:600;">
                                  Placed
                                </td>
                                <td width="33.33%" align="center" style="font-size:11px; color:#111827; font-weight:600;">
                                  In Production
                                </td>
                                <td width="33.33%" align="right" style="font-size:11px; color:#111827; font-weight:600;">
                                  Ready for Pickup
                                </td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                      </table>

                      <!-- Call to action -->
                      <table border="0" cellspacing="0" cellpadding="0" style="margin:0 0 24px 0;">
                        <tr>
                          <td>
                            <a href="${orderUrl}"
                               style="display:inline-block; background:rgb(90, 104, 114); color:#ffffff; font-size:14px; font-weight:600; text-decoration:none; padding:10px 22px; border-radius:999px; border:1px solid #111827;">
                              View Your Order
                            </a>
                          </td>
                        </tr>
                      </table>

                      <!-- Divider -->
                      <hr style="border:none; border-top:1px solid #e5e7eb; margin:16px 0 20px 0;" />

                      <!-- Order details header -->
                      <p style="margin:0 0 10px 0; font-size:12px; color:#6b7280; letter-spacing:0.16em; text-transform:uppercase;">
                        Order Summary
                      </p>

                      <!-- Order details block -->
                      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="border-collapse:collapse; margin-bottom:12px;">
                        <tr>
                          <td style="font-size:13px; color:#374151; padding:4px 0; width:40%;">Order number</td>
                          <td style="font-size:13px; color:#111827; padding:4px 0; text-align:right; font-weight:500;">
                            #${orderNumber}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size:13px; color:#374151; padding:4px 0;">Order date</td>
                          <td style="font-size:13px; color:#111827; padding:4px 0; text-align:right; font-weight:500;">
                            ${orderDate}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size:13px; color:#374151; padding:4px 0;">Customer</td>
                          <td style="font-size:13px; color:#111827; padding:4px 0; text-align:right; font-weight:500;">
                            ${customerName}
                          </td>
                        </tr>
                      </table>

                      <!-- Items table -->
                      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="border-collapse:collapse; margin:12px 0 18px 0; border:1px solid #e5e7eb; border-radius:10px; overflow:hidden;">
                        <tr style="background:#f9fafb;">
                          <th align="left" style="padding:10px 12px; font-size:12px; color:#6b7280; text-transform:uppercase; letter-spacing:0.12em; border-bottom:1px solid #e5e7eb;">
                            Item
                          </th>
                          <th align="center" style="padding:10px 8px; font-size:12px; color:#6b7280; text-transform:uppercase; letter-spacing:0.12em; border-bottom:1px solid #e5e7eb;">
                            Qty
                          </th>
                          <th align="right" style="padding:10px 12px; font-size:12px; color:#6b7280; text-transform:uppercase; letter-spacing:0.12em; border-bottom:1px solid #e5e7eb;">
                            Total
                          </th>
                        </tr>
                        ${itemsRowsHtml}
                      </table>

                      <!-- Totals -->
                      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="border-collapse:collapse; margin-bottom:18px;">
                        <tr>
                          <td style="font-size:13px; color:#6b7280; padding:2px 0;">Subtotal</td>
                          <td style="font-size:13px; color:#111827; padding:2px 0; text-align:right;">$${subtotalValue}</td>
                        </tr>
                        <tr>
                          <td style="font-size:13px; color:#6b7280; padding:2px 0;">Processing</td>
                          <td style="font-size:13px; color:#111827; padding:2px 0; text-align:right;">$${processingValue}</td>
                        </tr>
                        <tr>
                          <td style="font-size:13px; color:#111827; padding:8px 0; font-weight:600; border-top:1px solid #e5e7eb;">
                            Total
                          </td>
                          <td style="font-size:16px; color:#111827; padding:8px 0; text-align:right; font-weight:700; border-top:1px solid #e5e7eb;">
                            $${totalValue}
                          </td>
                        </tr>
                      </table>

                      <!-- Pickup details -->
                      <p style="margin:0 0 8px 0; font-size:12px; color:#6b7280; letter-spacing:0.14em; text-transform:uppercase;">
                        Pickup Details
                      </p>
                      <p style="margin:0 0 6px 0; font-size:13px; color:#4b5563; line-height:1.6;">
                        Pickup location:<br/>
                        <span style="font-weight:500; color:#111827;">See Our Designs</span><br/>
                        360 E Maple Rd, Suite M <br/>
                        Troy, MI 48083
                      </p>
                      <p style="margin:0 0 10px 0; font-size:13px; color:#4b5563; line-height:1.6;">
                        Pickup hours: <span style="font-weight:500; color:#111827;">Mon–Fri • 9 AM – 5 PM</span>
                      </p>

                      <!-- Info box -->
                      <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin:18px 0 0 0;">
                        <tr>
                          <td style="background:#f9fafb; border-radius:10px; border:1px solid #e5e7eb; padding:14px 14px 12px 14px;">
                            <p style="margin:0 0 4px 0; font-size:13px; color:#111827; font-weight:600;">
                              Need help with your order?
                            </p>
                            <p style="margin:0; font-size:13px; color:#4b5563; line-height:1.6;">
                              Just reply to this email or contact us at
                              <a href="mailto:cs@seeourdesigns.com" style="color:#111827; font-weight:600; text-decoration:none;">
                                cs@seeourdesigns.com
                              </a>.
                              Please include your order number so we can assist you faster.
                            </p>
                          </td>
                        </tr>
                      </table>

                    </td>
                  </tr>

                  <!-- Footer strip inside card -->
                  <tr>
                    <td style="padding:12px 20px 16px 20px; background:#f9fafb; border-top:1px solid #e5e7eb;">
                      <p style="margin:0; font-size:11px; color:#9ca3af; text-align:center; line-height:1.6;">
                        © 2025 See Our Designs · Troy, Michigan<br/>
                      </p>
                    </td>
                  </tr>

                </table>
              </td>
            </tr>

            <!-- Global footer -->
            <tr>
              <td style="padding-top:16px;">
                <p style="margin:0; font-size:11px; color:#9ca3af; text-align:center; font-family:-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
                  You’re receiving this email because you placed an order with See Our Designs.
                  If this wasn’t you, please contact
                  <a href="mailto:cs@seeourdesigns.com" style="color:#6b7280; text-decoration:underline;">support</a>.
                </p>
              </td>
            </tr>

          </table>
        </td>
      </tr>
    </table>
  </body>
</html>
    `,
    };
  }

  if (type === 'verification') {
    const safeName = (displayName || '').replace(/[<>]/g, '');
    const link = verificationLink || 'https://sodtf.com/verify-email';
    const subject = 'Verify your SODTF account';
    const html = `
      <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 640px; background-color: #f9f9f9; border-radius: 6px;">
        <h2 style="color: #333;">Verify your email${safeName ? `, ${safeName}` : ''}</h2>
        <p style="color: #555;">Tap the button below to confirm your SODTF account.</p>
        <p style="text-align: center; margin: 24px 0;">
          <a href="${link}" style="display: inline-block; padding: 12px 24px; background-color: #a7c667; color: #fff; border-radius: 8px; text-decoration: none; font-weight: bold;">
            Verify Email
          </a>
        </p>

        <p style="font-size: 12px; color: #bbb;">If you did not request this, you can ignore this email.</p>
      </div>
    `;
    return { subject, html };
  }

  return {
    subject: 'Thanks for your order! 🎉',
    html: `
      <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 600px; background-color: #f9f9f9; border-radius: 6px;">
        <h2 style="color: #333;">Thanks for your order! 🎉</h2>
        <p style="color: #555;">Your order has been received and is being processed. We'll notify you as soon as it's ready to go.</p>
        <hr style="border: none; border-top: 1px solid #ddd;" />
        <p style="font-size: 14px; color: #888;">If you have any questions, reply to this email or contact us at cs@seeourdesigns.com.</p>
        <p style="font-size: 12px; color: #bbb;">— The See Our Designs Team</p>
      </div>
    `,
  };
}

const EmailPreviewSection = ({ orderPreview }) => {
  const [selectedType, setSelectedType] = useState('order-confirmation');
  const [displayName, setDisplayName] = useState('Alex Customer');
  const [verificationLink, setVerificationLink] = useState('https://sodtf.com/verify-email?demo=1');

  const preview = React.useMemo(
    () => getEmailPreview(selectedType, { displayName, verificationLink, orderPreview }),
    [selectedType, displayName, verificationLink, orderPreview]
  );

  return (
    <Box
      sx={{
        mt: 4,
        mb: 4,
        maxWidth: '60rem',
        width: '70%',
        margin: '2rem auto',
        p: 3,
        borderRadius: 3,
        border: '2px dashed #94C940',
        backgroundColor: '#f4f4f4ff',
        boxShadow: '0px 4px 16px rgba(0,0,0,0.15)',
        fontFamily: 'Anek Latin',
      }}
    >
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 800, color: '#5A6872' }}>
        Email Preview
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: '#5A6872' }}>
        Use this section to preview how our transactional emails render.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            label="Email type"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            size="small"
            sx={{ mb: 2, backgroundColor: 'white' }}
          >
            {EMAIL_PREVIEW_TYPES.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {selectedType === 'verification' && (
            <>
              <TextField
                fullWidth
                label="Display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                size="small"
                sx={{ mb: 2, backgroundColor: 'white' }}
              />
              <TextField
                fullWidth
                label="Verification link"
                value={verificationLink}
                onChange={(e) => setVerificationLink(e.target.value)}
                size="small"
                sx={{ mb: 2, backgroundColor: 'white' }}
              />
            </>
          )}
        </Grid>
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: '#ffffff',
              border: '1px solid #d0d7de',
              minHeight: 200,
              overflow: 'auto',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#5A6872' }}>
              Subject: {preview.subject}
            </Typography>
            <Box
              sx={{
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor: '#f9f9f9',
              }}
              dangerouslySetInnerHTML={{ __html: preview.html }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Using client-side DPI conversion; no server-side DPI endpoint

const Checkout = () => {
  const { state } = useLocation(); // Grab the data sent through navigate
  // Safeguards if user lands directly without navigation state
  const safeTotalCost = state?.totalCost ?? 0;
  const sessionIdFromState = state?.sessionId ?? '';
  const {
    totalImages = 0,
    totalSquareInches = 0,
    canvasImages = [],
    pictures = [],
    costPerInch: incomingRate,
  } = state || {};


  // Log the order payload at initialization for debugging (state only; IDB logs later) ONCE on mount
  React.useEffect(() => {
    console.log("📝 Checkout initialized with payload (state only):", {
      sessionId: sessionIdFromState,
      totalImages,
      totalSquareInches,
      canvasImages,
      pictures,
      costPerInch: incomingRate,
      totalCost: safeTotalCost,
      previewMode: state?.previewMode || 'full'
    });
    // Using client-side DPI conversion during checkout (no server endpoint)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const [costPerInch, setCostPerInch] = useState(incomingRate || 0.04);
  const [net30Approved, setNet30Approved] = useState(false);

  const [chargeStripeFee, setChargeStripeFee] = useState(true);
  const [customerName, setCustomerName] = useState('');
  const stripe = useStripe();

  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [showPreviews, setShowPreviews] = useState(false);
  const [enlargedPreview, setEnlargedPreview] = useState(false)
  // For safe rendering of very large previews (convert data: -> blob:)
  const [previewObjectUrls, setPreviewObjectUrls] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  // Store created order id for overlay and navigation
  const [createdOrderId, setCreatedOrderId] = useState(null);
  // Error overlay state
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fatalErrorOverlay, setFatalErrorOverlay] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestSupported, setPaymentRequestSupported] = useState(false);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  // Removed per-request: no per-file percentage tracking, only count of files
  const [idbLoaded, setIdbLoaded] = useState(false);
  const [idbCanvasImages, setIdbCanvasImages] = useState([]);
  const [idbSheetGroups, setIdbSheetGroups] = useState([]);
  const [idbPictures, setIdbPictures] = useState([]);
  const [emailPreviewEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('previewEmails') === 'true';
    } catch {
      return false;
    }
  });
  // Ref to track if Stripe elements have been mounted
  const stripeElementsMounted = useRef(false);

  const incomingTotalCost = Number(safeTotalCost) || 0;
  const effectivePicturesList = React.useMemo(() => {
    if (Array.isArray(pictures) && pictures.length) return pictures;
    if (Array.isArray(idbPictures) && idbPictures.length) return idbPictures;
    return [];
  }, [pictures, idbPictures]);
  const calculatedSubtotal = React.useMemo(() => {
    if (!effectivePicturesList.length) return roundMoney(incomingTotalCost);
    const subtotal = effectivePicturesList.reduce((sum, img) => {
      const quantity = Math.max(1, Number((typeof img === 'object' && img?.quantity != null) ? img.quantity : 1) || 1);
      const widthIn = Number((typeof img === 'object' && img?.widthIn != null) ? img.widthIn : 0) || 0;
      const heightIn = Number((typeof img === 'object' && img?.heightIn != null) ? img.heightIn : 0) || 0;
      const { total } = computeRoundedItemPricing(widthIn, heightIn, costPerInch, quantity);
      return sum + total;
    }, 0);
    return roundMoney(subtotal);
  }, [effectivePicturesList, costPerInch, incomingTotalCost]);

  const emailOrderPreview = React.useMemo(() => {
    const pictureList = effectivePicturesList;

    const items = pictureList.map((img, index) => {
      const name = (typeof img === 'object' && img?.name) ? img.name : `Picture ${index + 1}`;
      const quantity = Math.max(
        1,
        Number(
          (typeof img === 'object' && img?.quantity != null)
            ? img.quantity
            : 1
        ) || 1
      );
      const widthIn = Number(
        (typeof img === 'object' && img?.widthIn != null)
          ? img.widthIn
          : 0
      ) || 0;
      const heightIn = Number(
        (typeof img === 'object' && img?.heightIn != null)
          ? img.heightIn
          : 0
      ) || 0;

      const dimensionLabel = (widthIn && heightIn)
        ? `${roundSize(widthIn).toFixed(2)}" × ${roundSize(heightIn).toFixed(2)}"`
        : '';

      const { total } = computeRoundedItemPricing(widthIn, heightIn, costPerInch, quantity);

      return {
        name,
        variant: dimensionLabel,
        sideDescription: 'DTF transfer',
        quantity,
        lineTotal: total,
      };
    });

    const subtotal = calculatedSubtotal;
    const processing = chargeStripeFee ? roundMoney(subtotal * 0.029 + 0.3) : 0;
    const total = roundMoney(subtotal + processing);

    const orderDateStr = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const fullOrderId = createdOrderId || '';
    const displayOrderNumber = fullOrderId
      ? String(fullOrderId).slice(0, 8)
      : `PREVIEW-${String(Date.now()).slice(-5)}`;

    return {
      customerName: customerName || name || '',
      orderId: fullOrderId,
      orderNumber: displayOrderNumber,
      orderDate: orderDateStr,
      subtotal,
      processing,
      total,
      items,
    };
  }, [effectivePicturesList, costPerInch, calculatedSubtotal, chargeStripeFee, createdOrderId, name, customerName]);
  // Money rounding is handled by utils/money.roundMoney

  // Fetch user discount on mount, but wait for auth.currentUser to be defined

  React.useEffect(() => {
    const mountCardElements = () => {
      if (!stripe || !elements || stripeElementsMounted.current) return;
      stripeElementsMounted.current = true;

      const cardNumber = elements.create('cardNumber');
      const cardExpiry = elements.create('cardExpiry');
      const cardCvc = elements.create('cardCvc');
      const postalCode = elements.create('postalCode');

      cardNumber.mount('#card-number');
      cardExpiry.mount('#card-expiry');
      cardCvc.mount('#card-cvc');
      postalCode.mount('#card-zip');
    };

    if (!net30Approved && authLoaded) {
      setTimeout(() => {
        mountCardElements();
      }, 0);
    }
  }, [stripe, elements, authLoaded, net30Approved]);
  // Use base64 sheets and pictures directly from state, or fall back to IDB

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('sessionId') || state?.sessionId || 'default';

    const needsCanvas = !Array.isArray(state?.canvasImages) || state.canvasImages.length === 0;
    const needsPics = !Array.isArray(state?.pictures) || state.pictures.length === 0;

    if (!(needsCanvas || needsPics)) { setIdbLoaded(true); return; }
    console.log("📦 Effective sources in Checkout:", {
      usingCanvasFrom: (Array.isArray(state?.canvasImages) && state.canvasImages.length) ? 'state' : 'idb',
      usingPicturesFrom: (Array.isArray(state?.pictures) && state.pictures.length) ? 'state' : 'idb',
      canvasCount: ((Array.isArray(state?.canvasImages) && state.canvasImages.length) ? state.canvasImages : idbCanvasImages)?.length || 0,
      pictureCount: ((Array.isArray(state?.pictures) && state.pictures.length) ? state.pictures : idbPictures)?.length || 0,
    });
    loadCheckoutPayload(sid)
      .then((data) => {
        if (!data) return;
        console.log("📥 Checkout received from IDB:", {
          sessionId: sid,
          hasData: !!data,
          canvasImagesCount: Array.isArray(data.canvasImages) ? data.canvasImages.length : 0,
          sheetGroupsCount: Array.isArray(data.sheetGroups) ? data.sheetGroups.length : 0,
          picturesCount: Array.isArray(data.pictures) ? data.pictures.length : 0,
          firstCanvasUrl:
            Array.isArray(data.canvasImages) && data.canvasImages[0]
              ? (typeof data.canvasImages[0] === 'string'
                ? data.canvasImages[0]
                : (data.canvasImages[0]?.url || ''))
              : 'none',
          firstCanvasUrlLength:
            Array.isArray(data.canvasImages) && data.canvasImages[0]
              ? (typeof data.canvasImages[0] === 'string'
                ? data.canvasImages[0].length
                : ((data.canvasImages[0]?.url || '').length))
              : 0
        });
        if (needsCanvas && Array.isArray(data.canvasImages)) setIdbCanvasImages(data.canvasImages);
        if (needsCanvas && Array.isArray(data.sheetGroups)) setIdbSheetGroups(data.sheetGroups);
        if (needsPics && Array.isArray(data.pictures)) setIdbPictures(data.pictures);
      })
      .catch((err) => console.warn('IDB load failed:', err))
      .finally(() => setIdbLoaded(true));
  }, []);

  React.useEffect(() => {
    const waitForAuth = async () => {
      while (!auth.currentUser) {
        await new Promise((res) => setTimeout(res, 100));
      }

      const uid = auth.currentUser.uid;
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data?.discount) setDiscountPercent(data.discount);
        if (data?.rate) setCostPerInch(data.rate);
        if (data?.net30Approved) setNet30Approved(data.net30Approved);
        const chargeStripeFee = data?.stripeFee ?? true;
        setChargeStripeFee(chargeStripeFee);
        const organizationName = (data?.freshbooksClientId?.organization || '').trim();
        const personalName = [
          data?.firstName || '',
          data?.lastName || '',
        ].filter(Boolean).join(' ').trim();
        const profileName = organizationName || personalName || '';
        if (profileName) setCustomerName(profileName);
        console.log("📦 Loaded user data:", data);
        setAuthLoaded(true);
      } else {
        setFatalErrorOverlay({
          code: 'CHK-002',
          title: 'Account Data Missing',
          message: 'We could not load your user profile. Please contact support.',
        });
      }
    };

    const handleAuthFailure = (err) => {
      console.error('CHK-001 auth bootstrap error', err);
      setFatalErrorOverlay({
        code: 'CHK-001',
        title: 'Checkout Initialisation Failed',
        message: err?.message || 'Unexpected error while preparing checkout.',
      });
    };

    waitForAuth().catch(handleAuthFailure);

    // Setup PaymentRequest for Apple Pay / Google Pay
    const setupPaymentRequest = async () => {
      if (!stripe) return;

      try {
        const baseTotal = calculatedSubtotal;
        const feeForPr = chargeStripeFee ? roundMoney(baseTotal * 0.029 + 0.3) : 0;
        const totalWithFee = roundMoney(baseTotal + feeForPr);

        const pr = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: net30Approved ? 'SODTF Net30 Order' : 'SODTF Stripe Order',
            amount: Math.round(totalWithFee * 100),
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        const result = await pr.canMakePayment();
        if (result) {
          setPaymentRequest(pr);
          setPaymentRequestSupported(true);
        }
      } catch (err) {
        console.warn('CHK-003 PaymentRequest setup failed', err);
        setFatalErrorOverlay((prev) => prev || {
          code: 'CHK-003',
          title: 'Payment Request Unavailable',
          message: 'Apple Pay / Google Pay could not be initialised on this device.',
        });
      }
    };

    setupPaymentRequest();

    // Mount individual Stripe card elements (after PaymentRequest setup)
    const mountCardElements = () => {
      if (!stripe || !elements || stripeElementsMounted.current) return;
      stripeElementsMounted.current = true;

      const cardNumber = elements.create('cardNumber');
      const cardExpiry = elements.create('cardExpiry');
      const cardCvc = elements.create('cardCvc');
      const postalCode = elements.create('postalCode');

      cardNumber.mount('#card-number');
      cardExpiry.mount('#card-expiry');
      cardCvc.mount('#card-cvc');
      postalCode.mount('#card-zip');
    };

    // Ensure Stripe elements mount after setup, only if net30Approved is false and authLoaded is true,
    // and defer mounting until DOM is ready.
    if (!net30Approved && authLoaded) {
      setTimeout(() => {
        mountCardElements();
      }, 0);
    }

  }, [stripe, elements]);

  // Helper: convert data URL to Blob without network requests (avoids data: fetch restrictions)
  async function dataUrlToBlob(url) {
    try {
      if (typeof url === 'string' && url.startsWith('data:')) {
        const commaIdx = url.indexOf(',');
        const header = url.substring(0, commaIdx);
        const data = url.substring(commaIdx + 1);
        const isBase64 = /;base64/i.test(header);
        const mimeMatch = header.match(/^data:([^;,]+)/i);
        const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
        let u8;
        if (isBase64) {
          const binStr = atob(data);
          u8 = new Uint8Array(binStr.length);
          for (let i = 0; i < binStr.length; i++) u8[i] = binStr.charCodeAt(i);
        } else {
          // Percent-encoded data
          const decoded = decodeURIComponent(data);
          u8 = new TextEncoder().encode(decoded);
        }
        return new Blob([u8], { type: mime });
      }
      // Fallback to fetch for http(s) and blob URLs
      const resp = await fetch(url);
      return await resp.blob();
    } catch (e) {
      console.error('dataUrlToBlob failed; rethrowing', e);
      throw e;
    }
  }

  // Helper: detect if a URL already points to Firebase Storage
  function isFirebaseStorageUrl(u = '') {
    if (typeof u !== 'string') return false;
    return (
      u.startsWith('gs://') ||
      u.includes('firebasestorage.googleapis.com') ||
      u.includes('storage.googleapis.com')
    );
  }


  // Basic mobile detection for conditional client behavior
  const isMobile = (typeof navigator !== 'undefined') && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!net30Approved && (!stripe || !elements)) return;

    setLoading(true);
    // Defer showing the upload overlay until we actually need to upload
    setUploading(false);
    // Use original names with IDB fallback (no *Src vars)
    // Expand sheetGroups if provided to recover per-sheet URLs (repeated by count)
    const expandGroups = (groups) => {
      if (!Array.isArray(groups)) return [];
      const out = [];
      groups.forEach(g => {
        const url = typeof g?.url === 'string' ? g.url : '';
        const count = Math.max(1, Number(g?.count) || 1);
        for (let i = 0; i < count; i++) out.push(url);
      });
      return out;
    };
    const idbExpandedFromGroups = expandGroups(idbSheetGroups);
    const effectiveCanvasImages = (Array.isArray(canvasImages) && canvasImages.length)
      ? canvasImages
      : (idbCanvasImages && idbCanvasImages.length ? idbCanvasImages : idbExpandedFromGroups);
    const effectivePictures = (Array.isArray(pictures) && pictures.length) ? pictures : idbPictures;

    // Disallow duplicate picture names within a single order.
    // This prevents two different sizes of the same art (e.g. 4x4 and 6x6)
    // from being stored under one shared name.
    const nameCounts = {};
    for (let i = 0; i < effectivePictures.length; i++) {
      const pic = effectivePictures[i];
      const rawName = typeof pic?.name === 'string' ? pic.name.trim() : '';
      if (!rawName) continue;
      const key = rawName.toLowerCase();
      nameCounts[key] = (nameCounts[key] || 0) + 1;
    }
    const hasDuplicateNames = Object.values(nameCounts).some((count) => count > 1);
    if (hasDuplicateNames) {
      setLoading(false);
      setUploading(false);
      setUploadTotal(0);
      setUploadedCount(0);
      setErrorCode('CHK-NAME');
      setErrorMessage('Pictures cannot share the same name. Please give each picture a unique name before checkout.');
      setShowErrorOverlay(true);
      return;
    }

    const storage = getStorage();
    const userId = auth.currentUser?.uid || 'anonymous';
    let uploadedImageURLs = [];

    // ---- Fast path: if all canvases already have Firebase URLs (and DPI handled earlier), skip uploads entirely
    const canvasUrls = (effectiveCanvasImages || []).map((img) =>
      typeof img === 'string' ? img : (img?.url || '')
    );
    const allCanvasAlreadyUploaded =
      canvasUrls.length > 0 && canvasUrls.every((u) => isFirebaseStorageUrl(u));

    if (allCanvasAlreadyUploaded) {
      uploadedImageURLs = canvasUrls;
      setUploadTotal(0);
      setUploadedCount(0);
      setUploading(false);
      console.log('🚀 Fast path: using existing Firebase sheet URLs; skipping uploads.', {
        count: uploadedImageURLs.length
      });
    }

    // Compute how many uploads we actually need to perform (canvas + pictures)
    const canvasUploadsNeeded = effectiveCanvasImages.reduce((count, image) => {
      const imageData = typeof image === 'string' ? image : image?.url || '';
      if (!imageData) return count;
      if (isFirebaseStorageUrl(imageData)) return count;
      return count + 1;
    }, 0);
    const pictureUploadsNeeded = effectivePictures.reduce((count, picture) => {
      const imageData = picture?.file || '';
      if (!imageData) return count;
      if (isFirebaseStorageUrl(imageData)) return count;
      return count + 1;
    }, 0);
    const totalUploadsNeeded = canvasUploadsNeeded + pictureUploadsNeeded;
    console.log('🧮 Uploads planned:', {
      canvasImagesProvidedBy: (Array.isArray(canvasImages) && canvasImages.length) ? 'state' : 'idb',
      picturesProvidedBy: (Array.isArray(pictures) && pictures.length) ? 'state' : 'idb',
      effectiveCanvasImages: Array.isArray(effectiveCanvasImages) ? effectiveCanvasImages.length : 0,
      effectivePictures: Array.isArray(effectivePictures) ? effectivePictures.length : 0,
      canvasUploadsNeeded,
      pictureUploadsNeeded,
      totalUploadsNeeded
    });
    setUploadTotal(totalUploadsNeeded);
    setUploading(totalUploadsNeeded > 0);
    setUploadedCount(0);

    if (allCanvasAlreadyUploaded) {
      console.log('🧵 Skipping canvas/picture uploads entirely; will submit existing Firebase URLs with order.');
    }
    if (!allCanvasAlreadyUploaded) {
      // --- Client upload path (only when explicitly enabled) ---
      // Canvas images: change DPI client-side and upload
      for (let i = 0; i < effectiveCanvasImages.length; i++) {
        const image = effectiveCanvasImages[i];
        const imageData = typeof image === 'string' ? image : (image?.url || '');
        if (!imageData) continue;

        if (imageData.startsWith('https://') && imageData.includes('firebasestorage.googleapis.com')) {
          uploadedImageURLs.push(imageData);
          continue;
        }

        const filePath = `orders/${userId}/${Date.now()}_${i}.png`;
        const storageRef = ref(storage, filePath);

        let dataUrl = typeof image === 'string' ? image : image.url;
        if (!dataUrl || !dataUrl.startsWith('data:')) {
          const resp = await fetch(dataUrl || imageData);
          const blob = await resp.blob();
          dataUrl = await new Promise(res => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(blob);
          });
        }

        const newDataUrl = await changeDpiDataUrl(dataUrl, 300);
        await uploadString(storageRef, newDataUrl, 'data_url');
        const downloadURL = await getDownloadURL(storageRef);
        uploadedImageURLs.push(downloadURL);
        setUploadedCount((prev) => prev + 1);
      }

    }

    // Pictures: upload as-is (even if canvases are already uploaded)
    // - Always upload on mobile to ensure server can access picture URLs
    // - Or upload when client pipeline is enabled
    for (let i = 0; i < effectivePictures.length; i++) {
      const picture = effectivePictures[i];
      const imageData = picture?.file;
      if (!imageData) continue;
      if (isFirebaseStorageUrl(imageData)) continue;

      const pictureName = picture.name || `${Date.now()}_${i}.png`;
      const filePath = `pictures/${userId}/${pictureName}`;
      const storageRef = ref(storage, filePath);

      let dataUrl = picture.file;
      if (!String(dataUrl).startsWith('data:')) {
        const resp = await fetch(dataUrl);
        const blob = await resp.blob();
        dataUrl = await new Promise(res => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result);
          reader.readAsDataURL(blob);
        });
      }

      // Persist the intended physical dimensions (if present) as custom metadata
      const metaDims = {};
      const wIn = Number(picture.widthIn);
      const hIn = Number(picture.heightIn);
      if (wIn && !Number.isNaN(wIn)) metaDims.widthIn = String(wIn);
      if (hIn && !Number.isNaN(hIn)) metaDims.heightIn = String(hIn);

      const metadata = Object.keys(metaDims).length
        ? { customMetadata: metaDims }
        : undefined;

      await uploadString(storageRef, dataUrl, 'data_url', metadata);
      const downloadURL = await getDownloadURL(storageRef);
      picture.file = downloadURL;
      setUploadedCount((prev) => prev + 1);
    }

    console.log("✅ net30Approved:", net30Approved);
    if (net30Approved) {
      // Use calculatedSubtotal for pictureSubtotal
      const pictureSubtotal = calculatedSubtotal;
      console.log("💸 Subtotal before discount:", pictureSubtotal);

      // Apply thousandths rounding to subtotal
      const localSubtotal = roundMoney(pictureSubtotal);
      console.log("🔻 Subtotal:", localSubtotal);

      // Apply thousandths rounding to stripe fee
      const stripeFee = chargeStripeFee
        ? roundMoney(localSubtotal * 0.029 + 0.3)
        : 0;
      console.log("💳 Stripe fee included?", chargeStripeFee, "→ Stripe fee:", stripeFee);

      // Apply thousandths rounding to final total
      const localTotal = roundMoney(localSubtotal + stripeFee);
      console.log("🧮 Stripe Total (subtotal + stripeFee):", localTotal);
      console.log("🧮 Net 30 Total:", localTotal);

      const token = !STRIPE_TEST_MODE && auth.currentUser
        ? await auth.currentUser.getIdToken()
        : null;
      if (!STRIPE_TEST_MODE && !token) {
        throw { code: 'AUTH_REQUIRED', message: 'You must be signed in to submit this order.' };
      }
      const buildGroupsFromUrls = (urls = []) => {
        const counts = new Map();
        urls.forEach(u => { if (u) counts.set(u, (counts.get(u) || 0) + 1); });
        return Array.from(counts.entries()).map(([url, quantity]) => ({ url, quantity }));
      };
      const effectiveGroups = (Array.isArray(idbSheetGroups) && idbSheetGroups.length)
        ? idbSheetGroups.map(g => ({ url: g.url, quantity: Math.max(1, Number(g.count || g.quantity) || 1) }))
        : buildGroupsFromUrls(uploadedImageURLs);

      const orderPayload = {
        customerId: auth.currentUser?.uid || 'unknown',
        images: effectiveGroups,
        quantity: effectiveCanvasImages.length,
        rate: costPerInch,
        subtotal: localSubtotal,
        // discount: discountPercent, // discount no longer included unless needed later
        total: localTotal,
        amountPaid: 0,
        invoiceNumber: Date.now().toString(),
        paymentTerms: 'Net 30',
        pictures: effectivePictures,
        ...(token ? { idToken: token } : {}),
      };
      console.log('Pictures array being sent to SubmitOrder:', effectivePictures);

      let submitSuccess = false;
      let lastSubmitError = null;
      for (const endpoint of SUBMIT_ORDER_ENDPOINTS) {
        if (!token && endpoint === REMOTE_SUBMIT_ORDER_URL && STRIPE_TEST_MODE) {
          continue;
        }
        try {
          const headers = {
            'Content-Type': 'application/json',
          };
          if (STRIPE_TEST_MODE && endpoint !== REMOTE_SUBMIT_ORDER_URL) {
            headers['X-Stripe-Test-Mode'] = 'true';
          }
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }
          const orderRes = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(orderPayload),
          });
          if (!orderRes.ok) {
            const text = await orderRes.text();
            throw new Error(text || `HTTP ${orderRes.status}`);
          }
          // Parse returned order id if present
          const json = await orderRes.json().catch(() => null);
          if (json && json.orderId) {
            setCreatedOrderId(json.orderId);
          }
          submitSuccess = true;
          break;
        } catch (err) {
          lastSubmitError = err;
          console.warn('Submit order request failed for', endpoint, err);
        }
      }

      if (!submitSuccess) {
        throw lastSubmitError || new Error('Failed to submit Net 30 order');
      }
      // Fire Google Analytics purchase event
      gtag('event', 'purchase', {
        transaction_id: orderPayload.invoiceNumber.toString(),
        value: localTotal,
        currency: 'USD',
      });
      // Cleanup stored checkout payload for this session
      try {
        const params = new URLSearchParams(window.location.search);
        const sid = params.get('sessionId');
        await removeCheckoutPayload(sid);
      } catch (_) { }
      setShowSuccessOverlay(true);
      setLoading(false);
      return;
    }

    try {
      // Use calculatedSubtotal for pictureSubtotal
      const pictureSubtotal = calculatedSubtotal;
      console.log("💸 Subtotal before discount:", pictureSubtotal);

      // Apply thousandths rounding to subtotal
      const localSubtotal = roundMoney(pictureSubtotal);
      console.log("🔻 Subtotal:", localSubtotal);

      // Apply thousandths rounding to stripe fee (only if enabled)
      const stripeFee = chargeStripeFee ? roundMoney(localSubtotal * 0.029 + 0.3) : 0;
      console.log("💳 Stripe fee included?", chargeStripeFee, "→ Stripe fee:", stripeFee);

      // Apply thousandths rounding to final total
      const localTotal = roundMoney(localSubtotal + stripeFee);
      console.log("🧮 Stripe Total (subtotal + stripeFee):", localTotal);

      // Log structured breakdown
      console.log("💰 Calculated totals:", {
        subtotal: localSubtotal,
        stripeFee,
        total: localTotal
      });

      const token = !STRIPE_TEST_MODE && auth.currentUser
        ? await auth.currentUser.getIdToken(true)
        : null;
      if (!STRIPE_TEST_MODE && !token) {
        throw { code: 'AUTH_REQUIRED', message: 'You must be signed in to complete payment.' };
      }
      let clientSecret = null;
      let paymentIntentId = null;
      let lastIntentError = null;
      const payload = {
        amount: Math.round(localTotal * 100),
        name,
        email: auth.currentUser?.email || '',
      };

      for (const endpoint of PAYMENT_INTENT_ENDPOINTS) {
        if (!token && endpoint === REMOTE_PAYMENT_INTENT_URL && STRIPE_TEST_MODE) {
          continue;
        }
        try {
          const intentRes = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(STRIPE_TEST_MODE ? { 'X-Stripe-Test-Mode': 'true' } : {}),
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(payload),
          });

          if (!intentRes.ok) {
            const text = await intentRes.text();
            throw new Error(text || `HTTP ${intentRes.status}`);
          }

          const json = await intentRes.json();
          if (json?.error) throw new Error(json.error);
          clientSecret = json.clientSecret;
          paymentIntentId = json.id;
          break;
        } catch (err) {
          lastIntentError = err;
          console.warn('Payment intent request failed for', endpoint, err);
        }
      }

      if (!clientSecret || !paymentIntentId) {
        throw lastIntentError || new Error('Failed to create payment intent');
      }

      // Confirm the payment using Stripe.js
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement('cardNumber'),
          billing_details: {
            name,
          }
        }
      });

      if (result.error) {
        const code = result.error.code || 'PAYMENT_INTENT_FAILED';
        throw { code, message: result.error.message || 'Payment confirmation failed' };
      }

      if (result.paymentIntent.status !== 'succeeded') {
        throw { code: 'PAYMENT_INTENT_FAILED', message: 'Payment not confirmed' };
      }
      console.log('Bearer token:', token);
      // Build grouped images for order payload (Stripe path)
      const buildGroupsFromUrlsStripe = (urls = []) => {
        const counts = new Map();
        urls.forEach(u => { if (u) counts.set(u, (counts.get(u) || 0) + 1); });
        return Array.from(counts.entries()).map(([url, quantity]) => ({ url, quantity }));
      };
      const effectiveGroups = (Array.isArray(idbSheetGroups) && idbSheetGroups.length)
        ? idbSheetGroups.map(g => ({ url: g.url, quantity: Math.max(1, Number(g.count || g.quantity) || 1) }))
        : buildGroupsFromUrlsStripe(uploadedImageURLs);
      // Submit order
      const orderPayload = {
        customerId: auth.currentUser?.uid || 'unknown',
        images: effectiveGroups,
        quantity: effectiveCanvasImages.length,
        rate: costPerInch,
        subtotal: localSubtotal,
        // discount: discountPercent, // discount no longer included unless needed later
        total: localTotal,
        paymentTerms: 'Stripe',
        amountPaid: localTotal,
        pictures: effectivePictures,
        invoiceNumber: Date.now().toString(),
        paymentIntentId,
        createdAt: new Date().toISOString(),
        idToken: token,
      };
      let submitSuccess = false;
      let lastSubmitError = null;
      let orderJson = null;
      for (const endpoint of SUBMIT_ORDER_ENDPOINTS) {
        if (!token && endpoint === REMOTE_SUBMIT_ORDER_URL && STRIPE_TEST_MODE) {
          continue;
        }
        try {
          const headers = {
            'Content-Type': 'application/json',
          };
          if (STRIPE_TEST_MODE && endpoint !== REMOTE_SUBMIT_ORDER_URL) {
            headers['X-Stripe-Test-Mode'] = 'true';
          }
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }
          const orderRes = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(orderPayload),
          });
          if (!orderRes.ok) {
            const text = await orderRes.text();
            throw new Error(text || `HTTP ${orderRes.status}`);
          }
          orderJson = await orderRes.json().catch(() => null);
          if (orderJson && orderJson.orderId) {
            setCreatedOrderId(orderJson.orderId);
          }
          submitSuccess = true;
          break;
        } catch (err) {
          lastSubmitError = err;
          console.warn('Submit order (Stripe) request failed for', endpoint, err);
        }
      }
      if (!submitSuccess) {
        throw lastSubmitError || { code: 'ORDER_SUBMIT_FAILED', message: 'Failed to submit order' };
      }
      // Fire Google Analytics purchase event
      gtag('event', 'purchase', {
        transaction_id: orderPayload.invoiceNumber,
        value: localTotal,
        currency: 'USD',
      });
      try {
        const params = new URLSearchParams(window.location.search);
        const sid = params.get('sessionId');
        await removeCheckoutPayload(sid);
      } catch (_) { }
      setShowSuccessOverlay(true);
      // navigate('/') -- removed to allow overlay usage
    } catch (err) {
      console.error('CHK-004 Error in checkout:', err);
      const code = err.code || 'ORDER_SUBMIT_FAILED';
      setErrorCode(code);
      setErrorMessage(ERROR_MESSAGES[code] || err.message || 'An unexpected error occurred.');
      setShowErrorOverlay(true);
      setFatalErrorOverlay({
        code: 'CHK-004',
        title: 'Checkout Submission Failed',
        message: err?.message || 'We hit an unexpected problem while submitting this order.',
      });
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  // Overlay for loading
  return (
    <div style={{
      height: 'stretch',
    }}>

      {fatalErrorOverlay && (
        <Box sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 1600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}>
          <Paper elevation={12} sx={{ maxWidth: 480, width: '100%', p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {fatalErrorOverlay.title || 'Checkout Error'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Code {fatalErrorOverlay.code}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {fatalErrorOverlay.message || 'Something unexpected happened.'}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setFatalErrorOverlay(null)}
            >
              Dismiss
            </Button>
          </Paper>
        </Box>
      )}

      {loading && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}>
          {/* Creative loader: glowing rotating ring + shimmering logo */}
          <Box sx={{ position: 'relative', width: 180, height: 180, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* rotating conic ring */}
            <Box sx={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: 'conic-gradient(from 0deg, rgba(148,201,64,0) 0%, rgba(148,201,64,.9) 25%, rgba(148,201,64,0) 50%, rgba(148,201,64,.9) 75%, rgba(148,201,64,0) 100%)',
              WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 14px), #000 0)',
              mask: 'radial-gradient(farthest-side, transparent calc(100% - 14px), #000 0)',
              animation: 'sodtf_spin 1.6s linear infinite',
              filter: 'drop-shadow(0 0 12px rgba(148,201,64,0.6))'
            }} />
            {/* inner soft glow */}
            <Box sx={{ position: 'absolute', inset: 18, borderRadius: '50%', background: 'radial-gradient(circle at 50% 50%, rgba(148,201,64,0.25), rgba(148,201,64,0) 60%)', filter: 'blur(8px)' }} />

            {/* logo with shimmer sweep */}
            <Box sx={{ position: 'relative', width: 120, height: 120, borderRadius: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={'/logo2.png'} alt="SODTF logo" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
              {/* shining sweep */}
              <Box sx={{
                position: 'absolute', top: -120, left: -60, width: '80px', height: '320px',
                transform: 'rotate(25deg)',
                background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.8) 50%, rgba(255,255,255,0) 100%)',
                mixBlendMode: 'screen',
                animation: 'sodtf_shine 1.8s ease-in-out infinite'
              }} />
            </Box>
          </Box>

          {/* subtle pulsing dots under the loader */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#94C940', animation: 'sodtf_pulse 1.2s ease-in-out infinite', animationDelay: '0s' }} />
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#94C940', animation: 'sodtf_pulse 1.2s ease-in-out infinite', animationDelay: '.2s' }} />
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#94C940', animation: 'sodtf_pulse 1.2s ease-in-out infinite', animationDelay: '.4s' }} />
          </Box>

          {/* keyframes for loader animations */}
          <style>{`
@keyframes sodtf_spin { to { transform: rotate(360deg); } }
@keyframes sodtf_pulse { 0%,100% { opacity:.45; transform: scale(1); } 50% { opacity:1; transform: scale(1.06); } }
@keyframes sodtf_shine { 0% { transform: translateX(-140px) rotate(25deg); } 100% { transform: translateX(250px) rotate(25deg); } }
`}</style>
          <Typography variant="h6">Processing, please do not close the tab.</Typography>
          <Typography variant="h6">This may take a while depending on how many images you have.</Typography>
          {uploading && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1">
                Uploading canvases: {uploadedCount} / {uploadTotal}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {showSuccessOverlay && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1301,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Box sx={{
            backgroundColor: '#ffffff',
            padding: '2.5rem 3rem',
            borderRadius: '12px',
            boxShadow: '0px 6px 30px rgba(0,0,0,0.4)',
            maxWidth: '500px',
            textAlign: 'center',
            fontFamily: 'Anek Latin',
            color: '#333',
          }}>
            <Typography variant="h4" sx={{ mb: 2, fontFamily: 'Anek Latin', color: '#A7C667', fontWeight: 'bold' }}>
              Success!
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: 'Anek Latin', mb: 1, color: '#5A6872' }}>
              Your payment and order have been successfully processed.
            </Typography>
            {createdOrderId && (
              <Typography variant="body1" sx={{ fontFamily: 'Anek Latin', mb: 2, color: '#5A6872' }}>
                Order #: <strong>{createdOrderId}</strong>
              </Typography>
            )}
            <Typography variant="body1" sx={{ fontFamily: 'Anek Latin', mb: 3, color: '#5A6872' }}>
              Thank you for your order. You’ll receive a confirmation email shortly and your invoice will be attached to your account.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              disabled={!createdOrderId}
              sx={{
                mb: 1,
                backgroundColor: '#A7C667',
                color: 'white',
                borderRadius: '8px',
                px: 3,
                py: 0,
                fontWeight: '1000',
                fontFamily: 'Anek Latin',
                fontSize: '1.2rem',
                textTransform: 'uppercase',
                '&:hover': { backgroundColor: '#A7C667' }
              }}
              onClick={() => {
                if (createdOrderId) navigate(`/profile/orders/${createdOrderId}`);
              }}
            >
              View Order
            </Button>
            <Button
              variant="outlined"
              color="primary"
              sx={{
                ml: 2,
                borderColor: '#A7C667',
                color: '#5A6872',
                borderRadius: '8px',
                px: 3,
                py: 0,
                fontWeight: '800',
                fontFamily: 'Anek Latin',
                fontSize: '1.0rem',
                textTransform: 'uppercase',
                '&:hover': { borderColor: '#A7C667' }
              }}
              onClick={() => navigate('/')}
            >
              Home
            </Button>
          </Box>
        </Box>
      )}
      {showErrorOverlay && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1301,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Box sx={{
            backgroundColor: '#ffffff',
            padding: '2.5rem 3rem',
            borderRadius: '12px',
            boxShadow: '0px 6px 30px rgba(0,0,0,0.4)',
            maxWidth: '500px',
            textAlign: 'center',
            fontFamily: 'Anek Latin',
            color: '#333',
          }}>
            <Typography variant="h4" sx={{ mb: 2, fontFamily: 'Anek Latin', color: '#D32F2F', fontWeight: 'bold' }}>
              Error {errorCode}
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'Anek Latin', mb: 3, color: '#5A6872' }}>
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{
                backgroundColor: '#D32F2F',
                color: 'white',
                borderRadius: '8px',
                px: 3,
                py: 0,
                fontWeight: '1000',
                fontFamily: 'Anek Latin',
                fontSize: '1.4rem',
                textTransform: 'uppercase',
                '&:hover': { backgroundColor: '#C62828' },
              }}
              onClick={() => {
                setShowErrorOverlay(false);
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          border: '4px solid #94C940',
          flexDirection: 'column',
          boxShadow: '0px 6px 30px rgba(0,0,0,0.2)',
          backgroundColor: '#f4f4f4ff',
          borderRadius: 3,
          gap: 4,
          flexWrap: 'wrap',
          maxWidth: "60rem",
          width: "70%",
          height: 'max-content',
          margin: '0 auto',
          marginTop: '1rem',
          color: '#5A6872',
          fontFamily: 'Anek Latin',
          position: 'relative' // Add this to position the back button
        }}>

        <Button
          onClick={() => {
            const params = new URLSearchParams(window.location.search);
            const sessionId = params.get('sessionId');
            navigate(`/dtf-sheet-builder?sessionId=${encodeURIComponent(sessionId)}`, {
              state: {
                sessionId: sessionIdFromState,
                pictures: (Array.isArray(pictures) && pictures.length) ? pictures : idbPictures,
                canvasImages: (Array.isArray(canvasImages) && canvasImages.length) ? canvasImages : idbCanvasImages
              }
            });
          }}
          sx={{
            position: 'absolute',
            top: '1.5rem',
            left: '1.5rem',
            backgroundColor: '#5A6872',
            fontFamily: 'Anek Latin',
            fontSize: '1rem',
            fontWeight: 1000,
            color: 'white',
            '&:hover': {
              backgroundColor: '#2e2e2eff',
            }
          }}
        >
          Back
        </Button>

        <div>
          <h1>Order Summary</h1>
        </div>
        <Divider sx={{ my: -4, color: '#5A6872' }}></Divider>
        <Grid container spacing={2} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Box sx={{
              p: 3,
              flex: 1,               // let this grow to fill</div>
              display: 'flex',       // make it a flex column
              flexDirection: 'column',
              justifyContent: 'space-between',
              fontSize: '1.3rem' // space the content & the button apart
            }}>
              <p>
                Total pictures: {effectivePicturesList
                  .reduce((acc, img) => {
                    const quantity = Math.max(1, Number((typeof img === 'object' && img?.quantity != null) ? img.quantity : 1) || 1);
                    return acc + quantity;
                  }, 0)}
              </p>
              <p>
                Cost per Square Inch: ${roundMoney(costPerInch).toFixed(2)}
              </p>
              {chargeStripeFee && (
                <p>Stripe Fee: ${roundMoney(calculatedSubtotal * 0.029 + 0.3).toFixed(2)}</p>
              )}

              <Box>
                <hr width="50%" />
              </Box>
              <Typography variant="h6">
                {(() => {
                  const subtotalDisp = calculatedSubtotal;
                  const feeDisp = chargeStripeFee ? roundMoney(subtotalDisp * 0.029 + 0.3) : 0;
                  const totalDisp = roundMoney(subtotalDisp + feeDisp);
                  return `Total Cost: $${roundMoney(totalDisp).toFixed(2)}`;
                })()}
              </Typography>

              <Box sx={{ mt: 5 /* optional gap */ }}>
                <Button
                  variant="contained"
                  onClick={() => setShowPreviews((prev) => !prev)}
                  sx={{
                    mb: 1,
                    mt: 2,
                    backgroundColor: '#5A6872',
                    color: 'white',
                    borderRadius: '8px',
                    px: 3,
                    py: 0,
                    fontWeight: '1000',
                    fontFamily: 'Anek Latin',
                    fontSize: '1.4rem',
                    textTransform: 'uppercase',
                    '&:hover': {
                      backgroundColor: '#2e2e2eff',
                    }
                  }}
                >
                  {showPreviews ? "Hide Pictures" : "View Pictures"}
                </Button>
              </Box>

              {showPreviews && (() => {
                const pictureList = effectivePicturesList;
                return (
                  <Box sx={{ mt: 2 }}>
                    {pictureList.length > 0 ? (
                      <Stack spacing={2}>
                        {pictureList.map((img, index) => {
                          const baseUrl = typeof img === 'string' ? img : (img?.file || img?.url || img?.src);
                          const displayUrl = (previewObjectUrls && previewObjectUrls[index]) ? previewObjectUrls[index] : baseUrl;
                          const name = (typeof img === 'object' && img?.name) ? img.name : `Picture ${index + 1}`;
                          const quantity = Math.max(1, Number((typeof img === 'object' && img?.quantity != null) ? img.quantity : 1) || 1);
                          const widthIn = Number((typeof img === 'object' && img?.widthIn != null) ? img.widthIn : 0) || 0;
                          const heightIn = Number((typeof img === 'object' && img?.heightIn != null) ? img.heightIn : 0) || 0;
                          const dimensionLabel = (widthIn && heightIn)
                            ? `${roundSize(widthIn).toFixed(2)}" × ${roundSize(heightIn).toFixed(2)}"`
                            : '—';
                          const { total: itemTotal } = computeRoundedItemPricing(widthIn, heightIn, costPerInch, quantity);
                          const hasImage = Boolean(displayUrl);

                          return (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'stretch',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                p: 2,
                                backgroundColor: '#fafafa',
                              }}
                            >
                              {hasImage ? (
                                <Box
                                  component="img"
                                  src={displayUrl}
                                  alt={name}
                                  onClick={() => displayUrl && setEnlargedPreview(displayUrl)}
                                  sx={{
                                    width: 96,
                                    height: 96,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px dashed #23232346',
                                    cursor: displayUrl ? 'pointer' : 'default',
                                    backgroundColor: '#f5f5f5',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 96,
                                    height: 96,
                                    borderRadius: 1,
                                    border: '1px dashed #23232346',
                                    backgroundColor: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#777',
                                    fontSize: '0.75rem',
                                    textAlign: 'center',
                                    px: 1,
                                  }}
                                >
                                  No preview
                                </Box>
                              )}
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Quantity: {quantity}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Dimensions: {dimensionLabel}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Total Price: ${roundMoney(itemTotal).toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography variant="body2">No pictures available</Typography>
                    )}
                  </Box>
                );
              })()}
              {/* Enlarged preview overlay */}
              {enlargedPreview && (
                <Box
                  onClick={() => setEnlargedPreview(false)}
                  sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    zIndex: 1302,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={enlargedPreview}
                    alt="Enlarged Preview"
                    style={{ maxWidth: '90%', maxHeight: '90%', border: '4px solid white', borderRadius: '12px' }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {!authLoaded && (
            <Box sx={{ flex: 1, minWidth: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress size={48} sx={{ color: '#94C940' }} />
            </Box>
          )}

          {authLoaded && !net30Approved && (
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Box sx={{ p: 3, backgroundColor: "transparent" }}>
                <h2 color='#5A6872' style={{ margin: '0 auto' }}>Enter Payment Info</h2>

                <form onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{
                    borderRadius: 2,
                    padding: 2,
                    mt: 1,
                    backgroundColor: 'transparent'

                  }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>

                      <Box id="card-number" sx={{ border: '2px solid #adb4baff', borderRadius: 2, p: 1, backgroundColor: 'white' }} />
                      <TextField
                        required
                        placeholder="Name on Card"
                        id='card-holder-name'
                        variant="outlined"
                        value={name}
                        onChange={(e) => setName(e.target.value)}

                        sx={{
                          mb: 2,
                          height: '20px',
                          borderRadius: 2,
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', height: '40px', color: '#000000', },
                          '& .MuiInputBase-input': { padding: '5px 5px 5px 5px', borderRadius: 2, border: '2px solid #adb4baff', color: '#000000', fontFamily: 'Anek Latin', fontSize: '1em' },
                          '& .MuiOutlinedInput-notchedOutline': { border: '#000000' },
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box id="card-expiry" sx={{ flex: 1, border: '2px solid #adb4baff', borderRadius: 2, p: 1, backgroundColor: 'white' }} />
                        <Box id="card-cvc" sx={{ flex: 1, border: '2px solid #adb4baff', borderRadius: 2, p: 1, backgroundColor: 'white' }} />
                      </Box>
                      <Box id="card-zip" sx={{ border: '2px solid #adb4baff', borderRadius: 2, p: 1, backgroundColor: 'white' }} />
                    </Box>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading || !stripe || !elements}
                    onClick={handleSubmit}
                    sx={{
                      backgroundColor: '#94C940',
                      color: 'white',
                      borderRadius: { xs: '13px', sm: '8px' },
                      py: 0.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontFamily: 'Anek Latin',
                      fontSize: '1.45rem',
                      '&:hover': { backgroundColor: '#94C940' },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Order'}
                  </Button>
                </form>
              </Box>
            </Box>
          )}
          {authLoaded && net30Approved && (
            <Box
              sx={{
                flex: 1,
                minWidth: 300,
                minHeight: '20%',
                display: 'flex',         // make outer box flex
                flexDirection: 'column',
              }}
            >
              <Box
                component="form"
                sx={{
                  p: 3,
                  flex: 1,               // let this grow to fill
                  display: 'flex',       // make it a flex column
                  flexDirection: 'column',
                  justifyContent: 'space-between', // space the content & the button apart
                }}
              >
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Submit Order with Net30
                  </Typography>
                  <Box>
                    You are approved for Net30, so you can submit your order without
                    immediate payment. Your invoice will be retrievable via Freshbooks
                    and attached to your account.
                  </Box>
                </Box>

                {/* This Box wraps the button and sits at the bottom */}
                <Box sx={{ mt: 2 /* optional gap */ }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading || uploading || !idbLoaded}
                    onClick={handleSubmit}
                    sx={{
                      backgroundColor: '#94C940',
                      color: 'white',
                      borderRadius: { xs: '13px', sm: '8px' },
                      py: 1,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontFamily: 'Anek Latin',
                      fontSize: '1.45rem',
                      '&:hover': { backgroundColor: '#94C940' },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Order'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Grid>
      </Box>
      {emailPreviewEnabled && (
        <EmailPreviewSection orderPreview={emailOrderPreview} />
      )}
    </div>
  );
};

export default function CheckoutPage() {
  return (
    <Elements stripe={stripePromise}>
      <Checkout />
    </Elements>
  );
}
